import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { useChat } from '../../../hooks/useChat';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

// Cache predictions per goal to avoid re-fetching
const predictionCache = {};

export default function GoalAIPrediction({ goal }) {
  const { user } = useAuth();
  const [prediction, setPrediction] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const { response, isLoading, sendMessage } = useChat('OPEN_AI', 'gpt-5', false);

  useEffect(() => {
    if (!goal?.id || !user?.id) return;
    if (predictionCache?.[goal?.id]) {
      setPrediction(predictionCache?.[goal?.id]);
      return;
    }

    // Fetch last 3 months of transactions to assess savings rate
    const now = new Date();
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1)?.toISOString();

    supabase?.from('transactions')?.select('amount, date, category')?.eq('user_id', user?.id)?.gte('date', threeMonthsAgo)?.then(({ data: txs }) => {
        if (!txs?.length) return;

        const totalSpent = txs?.reduce((s, t) => s + Math.abs(t?.amount || 0), 0);
        const avgMonthlySpend = totalSpent / 3;
        const target = parseFloat(goal?.target_amount) || 1;
        const current = parseFloat(goal?.current_amount) || 0;
        const monthly = parseFloat(goal?.monthly_contribution) || 0;
        const remaining = target - current;

        sendMessage([
          {
            role: 'system',
            content: `You are FinVisAR. Analyze a financial goal and return ONLY a valid JSON object with: "status" (exactly one of: "On track", "Behind", "At risk"), "monthsBehind" (number, 0 if on track), "reasoning" (1 sentence with specific numbers about savings rate vs required rate), "predictedDate" (predicted completion month/year as string like "Mar 2026"). Return ONLY the JSON object.`,
          },
          {
            role: 'user',
            content: `Assess goal: "${goal?.title}"\nTarget: $${target}\nSaved: $${current} (${((current / target) * 100)?.toFixed(1)}%)\nMonthly contribution: $${monthly}\nDeadline: ${goal?.deadline ? new Date(goal.deadline)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : 'None'}\nAvg monthly spending (last 3 months): $${avgMonthlySpend?.toFixed(0)}\nRemaining to save: $${remaining?.toFixed(0)}`,
          },
        ], { max_completion_tokens: 200 });
      });
  }, [goal?.id, user?.id]);

  useEffect(() => {
    if (response && !isLoading) {
      try {
        const match = response?.match(/\{.*\}/s);
        if (match) {
          const parsed = JSON.parse(match?.[0]);
          predictionCache[goal?.id] = parsed;
          setPrediction(parsed);
        }
      } catch {
        // ignore
      }
    }
  }, [response, isLoading, goal?.id]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.06)] animate-pulse">
        <div className="w-2 h-2 rounded-full bg-[rgba(255,255,255,0.1)]" />
        <div className="h-2.5 w-16 bg-[rgba(255,255,255,0.06)] rounded" />
      </div>
    );
  }

  if (!prediction) return null;

  const statusConfig = {
    'On track': { color: '#10B981', bg: 'rgba(16,185,129,0.1)', border: 'rgba(16,185,129,0.2)', icon: 'CheckCircle', label: 'On track ✓' },
    'Behind': { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.2)', icon: 'Clock', label: `${prediction?.monthsBehind}mo behind ⚠` },
    'At risk': { color: '#EF4444', bg: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.2)', icon: 'AlertCircle', label: 'At risk 🔴' },
  };

  const cfg = statusConfig?.[prediction?.status] || statusConfig?.['On track'];

  return (
    <div className="relative">
      <button
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all duration-200 cursor-default"
        style={{ color: cfg?.color, backgroundColor: cfg?.bg, border: `1px solid ${cfg?.border}` }}
      >
        <Icon name={cfg?.icon} size={10} color={cfg?.color} />
        {cfg?.label}
      </button>
      {/* Tooltip */}
      {showTooltip && (
        <div
          className="absolute bottom-full left-0 mb-2 w-56 p-3 rounded-xl z-50 text-left"
          style={{ background: '#0F172A', border: '1px solid rgba(0,229,255,0.2)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <Icon name="Bot" size={11} color="#00E5FF" />
            <span className="text-[10px] font-semibold text-[#00E5FF]">AI Prediction</span>
          </div>
          <p className="text-[#94A3B8] text-[11px] leading-relaxed mb-1.5">{prediction?.reasoning}</p>
          {prediction?.predictedDate && (
            <p className="text-[11px] font-medium" style={{ color: cfg?.color }}>Predicted: {prediction?.predictedDate}</p>
          )}
          {/* Tooltip arrow */}
          <div className="absolute top-full left-4 w-0 h-0" style={{ borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid rgba(0,229,255,0.2)' }} />
        </div>
      )}
    </div>
  );
}
